import config from 'config';
import {
  Mention,
  MutualConnection,
  PaginatedResponse,
  ProfileSocialOverview,
  SuggestionType,
} from 'types';
import { api } from './api';
import { usersApi } from './usersApi';

interface GetMentionListProps {
  keyword: string;
  offset: number;
  limit: number;
}

interface GetMutualFriendsProps {
  offset: number;
  limit: number;
  citizenId: string;
}

const GRAPHS_API = config.graphsAPI;
const SOCIAL_API = config.socialApi;

export const graphsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMentionList: build.query<PaginatedResponse<Mention>, GetMentionListProps>({
      query: ({ keyword, offset, limit }) => ({
        url: `${GRAPHS_API}/v3/citizens/search-mention/${keyword}?limit=${limit}&offset=${offset}`,
        method: 'GET',
        withAccessToken: true,
      }),
      transformResponse: (response: PaginatedResponse<Mention>, _, { offset, limit }) => {
        const updatedResponse = {
          ...response,
          page: offset / limit + 1,
        };
        return updatedResponse;
      },
    }),
    getFriendsCount: build.query<number, void>({
      query: () => ({
        url: `${GRAPHS_API}/influencers/me/friends?offset=0&limit=20`,
        headers: { 'x-api-version': '2.0' },
        withAccessToken: true,
      }),
      transformResponse: (response: PaginatedResponse<unknown>) => response.total,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: friendsCount } = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData('getMyProfile', undefined, (draft) => {
              Object.assign(draft, {
                ...draft,
                friendsCount,
              });
            })
          );
        } catch {}
      },
    }),
    getFollowingsCount: build.query<number, string>({
      query: (influencerId) => ({
        url: `${GRAPHS_API}/influencers/search-followed-by/${influencerId}?offset=0&limit=10`,
        headers: { 'x-api-version': '3.0' },
        withAccessToken: true,
      }),
      transformResponse: (response: PaginatedResponse<any>) => response.total,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: followingCount } = await queryFulfilled;
          dispatch(
            usersApi.util.updateQueryData('getMyProfile', undefined, (draft) => {
              Object.assign(draft, {
                ...draft,
                followingCount,
              });
            })
          );
        } catch {}
      },
    }),
    getMyProfileSocialInfo: build.query<ProfileSocialOverview, void>({
      query: () => ({
        url: `${GRAPHS_API}/influencers/me/social-overview`,
        withAccessToken: true,
      }),
    }),
    getRelevantCreators: build.query<
      PaginatedResponse<SuggestionType>,
      { offset: number; limit: number; citizenId?: string }
    >({
      query: ({ offset, limit, citizenId }) => ({
        url: citizenId
          ? `${GRAPHS_API}/influencers/profile-relevant-creators/${citizenId}?offset=${offset}&limit=${limit}`
          : `${SOCIAL_API}/v1/citizens/me/relevant-creators`,
        withAccessToken: true,
      }),
      transformResponse: (response: any, _, { offset, limit, citizenId }) => {
        return citizenId
          ? {
              ...response,
              page: offset / limit + 1,
            }
          : response;
      },
    }),
    getRelevantCitizens: build.query<
      PaginatedResponse<SuggestionType>,
      { offset: number; limit: number; citizenId?: string }
    >({
      query: ({ offset, limit, citizenId }) => ({
        url: citizenId
          ? `${GRAPHS_API}/influencers/profile-relevant-citizens/${citizenId}?offset=${offset}&limit=${limit}`
          : `${SOCIAL_API}/v1/citizens/me/relevant-citizens`,
        withAccessToken: true,
      }),
      transformResponse: (response: any, _, { offset, limit, citizenId }) => {
        return citizenId
          ? {
              ...response,
              page: offset / limit + 1,
            }
          : response;
      },
    }),
    getMutualFriends: build.query<PaginatedResponse<MutualConnection>, GetMutualFriendsProps>({
      query: ({ limit, offset, citizenId }) => ({
        url: `${GRAPHS_API}/influencers/mutual-following-connections/${citizenId}?offset=${offset}&limit=${limit}`,
        withAccessToken: true,
      }),
      transformResponse: (response: any, _, { offset, limit }) => {
        const updatedResponse = {
          ...response,
          page: offset / limit + 1,
        };
        return updatedResponse;
      },
    }),
  }),
});

export const {
  useGetMentionListQuery,
  useGetFriendsCountQuery,
  useGetFollowingsCountQuery,
  useGetMyProfileSocialInfoQuery,
  useGetMutualFriendsQuery,
  useGetRelevantCreatorsQuery,
  useGetRelevantCitizensQuery,
} = graphsApi;
